import React from 'react';

import { useJobDescriptionsFromExternalWebsitesState, useJobDescriptionsFromExternalWebsitesActions } from './JobDescriptionsFromExternalWebsitesContext';
import { cacheLineImportance, checkIfStillActive, syncJobBoard } from './restApi';

import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Dropdown from '../../Shared/UI/Dropdown/components/Dropdown';
import Button from '../../Shared/UI/Button/components/Button';

const JobDescriptionsFromExternalWebsitesTable = () => {
  const jobDescriptionsFromExternalWebsitesState = useJobDescriptionsFromExternalWebsitesState();
  const { openUpdateJobDescriptionFromExternalWebsiteModal, openCloseJobDescriptionFromExternalWebsiteModal, openReopenJobDescriptionFromExternalWebsiteModal, openDeleteJobDescriptionFromExternalWebsiteModal, openArchiveJobDescriptionFromExternalWebsiteModal, onResponse, onError } = useJobDescriptionsFromExternalWebsitesActions();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getDeletionStatus = (deletionQueuedAt) => {
    if (!deletionQueuedAt) return null;

    const queuedTime = new Date(deletionQueuedAt);
    const now = new Date();
    const minutesAgo = (now - queuedTime) / (1000 * 60);

    if (minutesAgo < 5) {
      return 'pending';
    } else {
      return 'stale';
    }
  };

  const getRowStyle = (deletionQueuedAt) => {
    const status = getDeletionStatus(deletionQueuedAt);

    if (status === 'pending') {
      return { backgroundColor: 'var(--color-status-warning-bg)' };
    } else if (status === 'stale') {
      return { backgroundColor: 'var(--color-status-error-bg)' };
    }

    return {};
  };

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Biopharma Group</Th>
          <Th>Title</Th>
          <Th>Processing State</Th>
          <Th>Link</Th>
          <Th>
            <div>Last Job Board Sync</div>
            <hr/>
            <div>Last Seen At</div>
            <hr/>
            <div>Closed At</div>
          </Th>
          <Th />
        </Tr>
      </Thead>
      <Tbody>
        {jobDescriptionsFromExternalWebsitesState?.jobDescriptionsFromExternalWebsites?.map(jobDescription => (
          <Tr key={jobDescription.id} style={getRowStyle(jobDescription.deletionQueuedAt)}>
            <Td>
              <a href={`/biopharma_groups/${jobDescription.biopharmaGroupId}`} target="_blank" rel="noopener noreferrer">
                {jobDescription.biopharmaGroupName}
              </a>
            </Td>
            <Td>{jobDescription.title}</Td>
            <Td>{jobDescription.processingState}</Td>
            <Td>
              <a href={jobDescription.sourceUrl} target="_blank" rel="noopener noreferrer">
                link
              </a>
            </Td>
            <Td>
              <div>{formatDate(jobDescription.externalJobBoardLastSyncedAt)}</div>
              <hr/>
              <div>{formatDate(jobDescription.lastSeenAt)}</div>
              <hr/>
              <div>{formatDate(jobDescription.closedAt)}</div>
            </Td>
            <Td>
              <Dropdown
                trigger={<Button variant="secondary" size="sm">Actions</Button>}
                align="right"
              >
                <Dropdown.Item onClick={() => openUpdateJobDescriptionFromExternalWebsiteModal(jobDescription)}>
                  Update
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    cacheLineImportance({
                      jobDescriptionId: jobDescription.id,
                      onResponse,
                      onError,
                    });
                  }}
                >
                  Cache Line Importance
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    checkIfStillActive({
                      jobDescriptionId: jobDescription.id,
                      onResponse,
                      onError,
                    });
                  }}
                >
                  Check If Still Active
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    syncJobBoard({
                      jobDescriptionId: jobDescription.id,
                      onResponse,
                      onError,
                    });
                  }}
                >
                  Sync Job Board
                </Dropdown.Item>
                <Dropdown.Divider />
                {!jobDescription.closedAt && (
                  <Dropdown.Item
                    onClick={() => openCloseJobDescriptionFromExternalWebsiteModal(jobDescription)}
                  >
                    Close
                  </Dropdown.Item>
                )}
                {jobDescription.closedAt && (
                  <Dropdown.Item
                    onClick={() => openReopenJobDescriptionFromExternalWebsiteModal(jobDescription)}
                  >
                    Reopen
                  </Dropdown.Item>
                )}
                {jobDescription.closedAt && (
                  <Dropdown.Item
                    onClick={() => openArchiveJobDescriptionFromExternalWebsiteModal(jobDescription)}
                    destructive
                  >
                    Archive
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  onClick={() => openDeleteJobDescriptionFromExternalWebsiteModal(jobDescription)}
                  destructive
                >
                  Delete
                </Dropdown.Item>
              </Dropdown>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

export default JobDescriptionsFromExternalWebsitesTable;
